import type React from "react";

import type { MessageStatusChangeRequestV2 } from "@/api/types";
import { Blockquote } from "@/components/Blockquote/Blockquote";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { getClosePostReasonLabel } from "@/helpers/community-feed";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useTranslation } from "@/translations";

type CommunityMessageClosePostConfirmModalProps = ModalBaseProps & {
  messageId: string;
  reason: MessageStatusChangeRequestV2["reason"];
  canArchive: boolean;
  canDelete: boolean;
};

export function CommunityMessageClosePostConfirmModal({
  messageId,
  reason,
  canArchive,
  canDelete,
  isOpened,
  onOpenChange,
}: CommunityMessageClosePostConfirmModalProps): React.ReactNode {
  const { t } = useTranslation();

  const updateMessageStatus = communityFeedMutations.useUpdateMessageStatus();

  const remark = canArchive
    ? t("component.community-post.close-post-confirm-modal.archive.remark")
    : t("component.community-post.close-post-confirm-modal.delete.remark");

  const handleDelete = async () => {
    await updateMessageStatus.mutateAsync({
      messageId,
      data: {
        newStatus: "deleted",
        reason,
      },
    });

    onOpenChange(false);
  };

  const handleArchive = async () => {
    await updateMessageStatus.mutateAsync({
      messageId,
      data: {
        newStatus: "archived",
        reason,
      },
    });

    onOpenChange(false);
  };

  return (
    <Modal.Root
      title={t("component.community-post.close-post-confirm-modal.title")}
      size="sm"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <section className="flex flex-col py-space-sm">
          <h4 className="text-body">{t("component.community-post.close-post-confirm-modal.body")}</h4>
          <Blockquote> {getClosePostReasonLabel({ reasonType: reason, t })}</Blockquote>
          <p className="text-caption text-text-secondary">{remark}</p>
        </section>
      </Modal.Body>
      <Modal.Actions>
        {canDelete && (
          <Button
            data-testid="modal-confirm-delete"
            variant={canArchive ? "outline" : "filled"}
            tone="error"
            onClick={handleDelete}
            isLoading={updateMessageStatus.isPending}
          >
            {t("component.community-post.close-post-confirm-modal.btn.delete-post")}
          </Button>
        )}
        {canArchive && (
          <Button
            data-testid="confirm-archiving-btn"
            variant="filled"
            onClick={handleArchive}
            isLoading={updateMessageStatus.isPending}
          >
            {t("component.community-post.close-post-confirm-modal.btn.archive-post")}
          </Button>
        )}
      </Modal.Actions>
    </Modal.Root>
  );
}
